import React from 'react';
import { get, post } from 'axios';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Stack,
  Text,
  Image,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import colors from './onboarding-colors';
import bgImage from '../../assets/venice-event-home-bg.png';
import VeniceLogo from '../../assets/default/venice-hardcode.png';
import { dataApiUrl } from 'utils/url';
import { kycEvent, purchaseEvent } from 'data-client/analytics';

const INSTAGRAM = 'instagram';
const YOUTUBE = 'youtube';
const TIKTOK = 'tiktok';

const sendAnalyticsEvents = stripeSessionData => {
  const {
    amount_total,
    currency,
    discounts,
    id: transactionId,
    subscription: subscriptionId,
    payment_link,
    customer_details,
  } = stripeSessionData || {};

  const tier = {
    tierId: payment_link,
    tierName: 'Venice Pro',
    price: amount_total,
  };

  const couponCode = discounts?.[0]?.coupon?.id;

  const { name, email } = customer_details || {};

  kycEvent(name, email, subscriptionId);
  purchaseEvent(tier, couponCode, currency, transactionId);
};

const fetchUserData = async sessionId => {
  const { data } = await get(`${dataApiUrl()}/memberships/customer/session`, {
    params: { stripeSessionId: sessionId },
  });

  const { name, email } = data?.data?.customer_details || {};

  sendAnalyticsEvents(data?.data);

  return { name, email, subscriptionId: data?.data?.subscription };
};

const createNewMembership = async ({ stripeSessionId, password, spotifyArtist, chartmetricId, socials }) => {
  const response = await post(`${dataApiUrl()}/memberships/customer/new-membership`, {
    stripeSessionId,
    password,
    spotifyId: spotifyArtist?.id,
    spotifyName: spotifyArtist?.name,
    chartmetricId: chartmetricId ? chartmetricId : undefined,
    socials,
    getAuth0Token: true,
  });

  return response?.data?.data;
};

const PasswordInput = ({ show, handleClick, value, onChange }) => (
  <FormControl id="password">
    <FormLabel fontFamily="Manrope, sans-serif" fontSize="0.875rem" fontWeight="medium" color={colors.text.primary}>
      Password
    </FormLabel>
    <InputGroup>
      <Input
        type={show ? 'text' : 'password'}
        placeholder="•••••••••••••"
        bg={colors.brown[100]}
        borderColor={colors.brown[500]}
        _hover={{ borderColor: colors.brown[700] }}
        _focus={{ borderWidth: '2px', borderColor: colors.brown[900], boxShadow: `0 0 0 3px ${colors.accent[900]}` }}
        fontFamily="Manrope, sans-serif"
        fontSize="0.875rem"
        value={value}
        onChange={onChange}
      />
      <InputRightElement>
        <IconButton
          size="sm"
          variant="ghost"
          onClick={handleClick}
          aria-label={show ? 'Hide password' : 'Show password'}
          icon={show ? <ViewOffIcon /> : <ViewIcon />}
          color={colors.text.secondary}
          _hover={{ bg: colors.transparent, color: colors.accent[500] }}
        />
      </InputRightElement>
    </InputGroup>
  </FormControl>
);

const SpotifyArtistCard = ({ artist, onSelect }) => (
  <Box p={1} _hover={{ backgroundColor: colors.brown[500] }} cursor="pointer" onClick={onSelect}>
    <Flex align="center" justify="space-between" gap={3}>
      <Flex align="center">
        {artist?.imageUrl?.length > 0 ? (
          <Image src={artist?.imageUrl} alt={artist?.name} borderRadius="full" boxSize="1.625rem" mr="0.5rem" />
        ) : (
          <Box
            height="1.625rem"
            width="1.625rem"
            borderRadius="0.813rem"
            backgroundColor={colors.gray[500]}
            mr="0.5rem"
          />
        )}
        <Text fontSize="0.875rem" fontFamily="Manrope, sans-serif" color={colors.text.dark}>
          {artist?.name}
        </Text>
      </Flex>
      <Button
        borderRadius="1rem"
        size="small"
        fontSize="0.75rem"
        p="0.25rem 0.5rem"
        bg={colors.brown[500]}
        fontWeight="normal"
        color={colors.text.dark}
        border={`1px solid ${colors.brown[300]}`}>
        Select
      </Button>
    </Flex>
  </Box>
);

const SelectedArtistCard = ({ artist, onChangeClick }) => (
  <Box p={3} bg={colors.brown[100]} borderRadius="md" mb={4}>
    <Flex justify="space-between" align="center">
      <Flex align="center" gap={3}>
        <Image src={artist?.imageUrl} alt={artist?.name} borderRadius="full" boxSize="1.625rem" />
        <Box>
          <Text fontSize="0.875rem" fontWeight="medium" color={colors.text.primary}>
            {artist?.name}
          </Text>
          <Text fontSize="0.75rem" color={colors.text.secondary}>
            Spotify Artist
          </Text>
        </Box>
      </Flex>
      <Button size="sm" variant="outline" colorScheme={colors.brown[900]} onClick={onChangeClick}>
        Change
      </Button>
    </Flex>
  </Box>
);

const SignupStep = ({ show, handleClick, password, setPassword, email = '' }) => (
  <Stack spacing={6}>
    <Text fontSize="1.5rem" fontWeight="medium" fontFamily="Playfair Display, serif" color={colors.text.primary}>
      Let's setup your account
    </Text>

    <FormControl id="email">
      <FormLabel fontFamily="Manrope, sans-serif" fontSize="0.875rem" fontWeight="medium" color={colors.text.primary}>
        Email
      </FormLabel>
      <Input
        type="email"
        value={email}
        isDisabled
        bg={colors.brown[100]}
        borderColor={colors.brown[500]}
        _hover={{ borderColor: colors.brown[700] }}
        _focus={{ borderWidth: '2px', borderColor: colors.brown[900], boxShadow: `0 0 0 3px ${colors.accent[900]}` }}
        fontFamily="Manrope, sans-serif"
        fontSize="0.875rem"
      />
    </FormControl>

    <PasswordInput show={show} handleClick={handleClick} value={password} onChange={e => setPassword(e.target.value)} />
  </Stack>
);

const SpotifyStep = ({
  spotifyArtists,
  spotifyId,
  setSpotifyId,
  selectedSpotifyArtist,
  handleSelectSpotifyArtist,
  handleChangeSelectedSpotifyArtist,
  handleNext,
}) => (
  <Stack spacing={6}>
    <Text fontSize="1.5rem" fontWeight="medium" fontFamily="Playfair Display, serif" color={colors.text.primary}>
      Connect Spotify
    </Text>

    {selectedSpotifyArtist ? (
      <SelectedArtistCard artist={selectedSpotifyArtist} onChangeClick={handleChangeSelectedSpotifyArtist} />
    ) : (
      <FormControl>
        <FormLabel fontFamily="Manrope, sans-serif" fontSize="0.875rem" fontWeight="medium" color={colors.text.primary}>
          What's your artist name?
        </FormLabel>
        <Input
          value={spotifyId}
          onChange={e => setSpotifyId(e.target.value)}
          placeholder="Search for your artist profile..."
          bg={colors.white}
          borderColor={colors.brown[500]}
          _hover={{ borderColor: colors.brown[700] }}
          _focus={{ borderColor: colors.brown[900], boxShadow: `0 0 0 3px ${colors.accent[900]}` }}
        />
      </FormControl>
    )}

    {spotifyId && !selectedSpotifyArtist && (
      <Box position="relative" maxH="18.75rem" zIndex="10">
        <Box
          maxH="12.5rem"
          borderRadius="0.5rem"
          overflowY="scroll"
          position="absolute"
          top="-0.75rem"
          backgroundColor={colors.brown[100]}
          border={`1px solid ${colors.gray[300]}`}
          width="100%">
          {spotifyArtists?.map(artist => (
            <SpotifyArtistCard artist={artist} key={artist.id} onSelect={() => handleSelectSpotifyArtist(artist)} />
          ))}
        </Box>
      </Box>
    )}

    {!selectedSpotifyArtist && (
      <Button
        variant="ghost"
        color={colors.text.secondary}
        onClick={() => {
          setSpotifyId('');
          handleNext();
        }}>
        Skip this step
      </Button>
    )}
  </Stack>
);

const SocialsStep = ({ socials, setSocials, selectedSpotifyArtist, handleChangeSelectedSpotifyArtist }) => {
  const handleChangeSocials = (e, platform) => {
    e.persist();
    setSocials(prev => ({ ...prev, [platform]: e.target.value }));
  };

  return (
    <Stack spacing={6}>
      <Text fontSize="1.5rem" fontWeight="medium" fontFamily="Playfair Display, serif" color={colors.text.primary}>
        Your social profiles
      </Text>

      {selectedSpotifyArtist && (
        <SelectedArtistCard artist={selectedSpotifyArtist} onChangeClick={handleChangeSelectedSpotifyArtist} />
      )}

      {[INSTAGRAM, YOUTUBE, TIKTOK].map(platform => (
        <FormControl key={platform}>
          <FormLabel
            fontFamily="Manrope, sans-serif"
            fontSize="0.875rem"
            fontWeight="medium"
            color={colors.text.primary}>
            {platform.charAt(0).toUpperCase() + platform.slice(1)} Handle
          </FormLabel>
          <Input
            value={socials[platform]}
            onChange={e => handleChangeSocials(e, platform)}
            placeholder="@yourhandle"
            bg={colors.white}
            borderColor={colors.brown[500]}
            _hover={{ borderColor: colors.brown[700] }}
            _focus={{ borderColor: colors.accent[500] }}
          />
        </FormControl>
      ))}
    </Stack>
  );
};

const ImageSection = () => (
  <Box w="100%" flex="4" position="relative" minH={{ base: '60vh', md: '100vh' }}>
    <Box w="100%" h="100vh" overflow="hidden" position="relative">
      <Image
        alt="Party with disco balls"
        objectFit="cover"
        src={bgImage}
        layout="fill"
        position="absolute"
        height="100%"
        width="100%"
        transform="scale(1.1)"
      />
    </Box>

    <Box position="absolute" top="6" left="6" zIndex="2">
      <Image src={VeniceLogo} width="8rem" />
    </Box>

    <Box
      position="absolute"
      bottom="3rem"
      left="2rem"
      color={colors.white}
      opacity="0.7"
      maxWidth="16rem"
      textShadow="1px 1px 3px rgba(0,0,0,0.5)">
      <Text fontSize="1rem" mb={2} fontFamily="Manrope, sans-serif" lineHeight="130%">
        Let's get to know more about you so we can set expectations and personalize your strategy.
      </Text>
    </Box>
  </Box>
);

export {
  fetchUserData,
  createNewMembership,
  PasswordInput,
  SpotifyArtistCard,
  SelectedArtistCard,
  SignupStep,
  SpotifyStep,
  SocialsStep,
  ImageSection,
};
